import { useState } from "react";
import axios from "axios";
import { FileBarChart, Map, User, Loader2, Calendar, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REPORT_TYPES = [
  {
    id: "daily_threat",
    label: "Daily Threat Brief",
    icon: FileBarChart,
    desc: "Top 5 threats by entity for a given date",
    fields: [
      { key: "date", label: "Date", type: "date", default: new Date().toISOString().split("T")[0] },
    ],
  },
  {
    id: "geo_pattern",
    label: "Geospatial Pattern",
    icon: Map,
    desc: "GeoJSON heatmap with linked SIGINT/SOCMINT snippets",
    fields: [
      { key: "lat_min", label: "Lat Min", type: "number", default: -26 },
      { key: "lat_max", label: "Lat Max", type: "number", default: -25 },
      { key: "lon_min", label: "Lon Min", type: "number", default: 28 },
      { key: "lon_max", label: "Lon Max", type: "number", default: 29 },
      { key: "time_start", label: "Time Start", type: "datetime-local", default: "2026-05-01T00:00" },
      { key: "time_end", label: "Time End", type: "datetime-local", default: "2026-05-11T00:00" },
    ],
  },
  {
    id: "entity_profile",
    label: "Entity Profile",
    icon: User,
    desc: "Timeline + relationship graph for an entity",
    fields: [
      { key: "entity_name", label: "Entity Name", type: "text", default: "" },
    ],
  },
];

export default function ImsReports() {
  const [reportType, setReportType] = useState("daily_threat");
  const [params, setParams] = useState({});
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);

  const activeReport = REPORT_TYPES.find(r => r.id === reportType);

  const handleTypeChange = (id) => {
    setReportType(id);
    setParams({});
    setResult(null);
  };

  const handleParamChange = (key, value) => {
    setParams(prev => ({ ...prev, [key]: value }));
  };

  const generate = async () => {
    setGenerating(true);
    setResult(null);

    try {
      const body = { ...params };
      // Convert datetime-local to ISO
      if (body.time_start) body.time_start = new Date(body.time_start).toISOString();
      if (body.time_end) body.time_end = new Date(body.time_end).toISOString();

      const res = await axios.post(`${API}/ims/reports/${reportType}`, body, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setResult(res.data);
      toast.success("Report generated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Report generation failed");
    }
    setGenerating(false);
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
            <FileBarChart className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-800 tracking-wide">
              REPORTS
            </h1>
            <p className="text-slate-600">Generate intelligence reports</p>
          </div>
        </div>
      </div>

      {/* Report Type Selection */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        {REPORT_TYPES.map((rt) => {
          const Icon = rt.icon;
          const isActive = reportType === rt.id;
          return (
            <button
              key={rt.id}
              onClick={() => handleTypeChange(rt.id)}
              className={`text-left p-4 border transition-all ${
                isActive
                  ? 'bg-emerald-50 border-emerald-400 border-2'
                  : 'bg-white border-slate-200 hover:border-emerald-300'
              }`}
            >
              <Icon className={`w-6 h-6 mb-2 ${isActive ? 'text-emerald-600' : 'text-slate-500'}`} />
              <p className={`font-semibold ${isActive ? 'text-emerald-800' : 'text-slate-700'}`}>{rt.label}</p>
              <p className="text-xs text-slate-500 mt-1">{rt.desc}</p>
            </button>
          );
        })}
      </div>

      {/* Parameters */}
      <div className="bg-white border border-slate-200 p-6 mb-6">
        <h3 className="font-semibold text-slate-800 mb-4">{activeReport.label} — Parameters</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {activeReport.fields.map((f) => (
            <div key={f.key}>
              <label className="block text-xs font-medium text-slate-500 mb-1">{f.label}</label>
              <Input
                type={f.type}
                value={params[f.key] ?? f.default}
                onChange={(e) => handleParamChange(f.key, e.target.value)}
                className="rounded-none border-slate-300"
              />
            </div>
          ))}
        </div>
        <Button
          onClick={generate}
          disabled={generating}
          className="mt-4 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 rounded-none"
        >
          {generating ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
          ) : (
            <><FileBarChart className="w-4 h-4 mr-2" /> Generate Report</>
          )}
        </Button>
      </div>

      {/* Result */}
      {result && (
        <div className="bg-white border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-800 mb-4">
            Report — {result.report_type || reportType}
          </h3>

          {/* Graph image link */}
          {result.content?.graph_image && (
            <div className="mb-4">
              <a
                href={`${API}/ims/output/${result.content.graph_image}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-500 text-sm font-medium"
              >
                <ExternalLink className="w-4 h-4" />
                View Entity Graph
              </a>
            </div>
          )}

          {/* Summary prose for daily threat */}
          {result.content?.summary_prose && (
            <div className="mb-4 p-3 bg-app border border-slate-200 text-sm text-slate-700">
              {result.content.summary_prose}
            </div>
          )}

          {/* Top 5 threats */}
          {result.content?.top_5_threats && (
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-slate-600 mb-2">Top Threats</h4>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-slate-500">
                    <th className="pb-2">Entity</th>
                    <th className="pb-2">Mentions</th>
                    <th className="pb-2">Avg Threat</th>
                  </tr>
                </thead>
                <tbody>
                  {result.content.top_5_threats.map((t, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="py-2 font-medium text-slate-800">{t.entity_name}</td>
                      <td className="py-2">{t.mention_count}</td>
                      <td className="py-2">{t.avg_threat_score?.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* GeoJSON points */}
          {result.content?.total_points !== undefined && (
            <div className="mb-4 text-sm text-slate-600">
              <span className="font-semibold">{result.content.total_points}</span> geospatial points found
            </div>
          )}

          {/* Timeline */}
          {result.content?.timeline && result.content.timeline.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-slate-600 mb-2">Timeline</h4>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {result.content.timeline.map((t, i) => (
                  <div key={i} className="flex items-center gap-3 text-xs text-slate-600 p-2 bg-app">
                    <Calendar className="w-3 h-3 flex-shrink-0" />
                    <span className="font-mono w-36">{t.timestamp?.slice(0, 19).replace("T", " ")}</span>
                    <span className="text-slate-400 w-20">{t.entity_type}</span>
                    <span className="font-semibold">{t.threat_score?.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Raw content */}
          <details>
            <summary className="text-sm text-slate-500 cursor-pointer hover:text-slate-700">Raw JSON</summary>
            <pre className="mt-2 p-3 bg-app text-xs font-mono whitespace-pre-wrap max-h-96 overflow-auto">
              {JSON.stringify(result, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
}
